import { Reveal, MaskLines, Label, ImageReveal, Branch, PartialCircle } from "./Reveal-graphics";

const STAGES = [
  {
    n: "01",
    name: "Origin",
    statement: "Place matters.",
    body: "Coffee begins with geography — the landscape, climate and growing conditions that surround it.",
    images: [
      { src: "https://images.unsplash.com/photo-1663597675745-96a3f784369e?q=80&w=1600&auto=format&fit=crop", alt: "Coffee growing in green hills", className: "h-72 md:h-[420px]" },
    ],
    testid: "stage-origin",
  },
  {
    n: "02",
    name: "Process",
    statement: "Every decision leaves a mark.",
    body: "From cherry to green coffee to roasted coffee, each step shapes what the cup will become.",
    images: [
      { src: "https://images.unsplash.com/photo-1500762736728-b34f37b78c7b?q=80&w=1200&auto=format&fit=crop", alt: "Hands holding freshly picked coffee cherries", className: "h-64 md:h-[340px]" },
      { src: "https://images.unsplash.com/photo-1511537190424-bbbab87ac5eb?q=80&w=1200&auto=format&fit=crop", alt: "Roasting coffee beans", className: "h-64 md:h-[340px]" },
    ],
    testid: "stage-process",
  },
  {
    n: "03",
    name: "Release",
    statement: "Here for a season.",
    body: "Anichya coffees are released in limited lots. Each one belongs to a particular harvest and a particular moment. When it is gone, something new takes its place.",
    images: [
      { src: "https://images.unsplash.com/photo-1765144815892-1925a0def4a2?q=80&w=1200&auto=format&fit=crop", alt: "Green coffee beans under a magnifying glass", className: "h-72 md:h-[420px]" },
    ],
    testid: "stage-release",
  },
];

const STEPS = ["Select", "Roast", "Release", "Enjoy", "Gone"];

export default function CoffeeStory() {
  return (
    <section id="coffee" className="relative overflow-hidden py-28 md:py-40" data-testid="coffee-story-section">
      <Branch className="absolute -left-24 top-[54%] w-52 md:w-72 opacity-40 pointer-events-none" />
      <PartialCircle className="absolute -right-28 bottom-16 w-64 md:w-96 text-forest opacity-25 pointer-events-none" />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="max-w-[1200px] mx-auto">
          <Label>02 — The coffee</Label>
          <div className="mt-10 grid md:grid-cols-12 gap-10 items-end">
            <h2 className="md:col-span-6 font-serif font-normal text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-[-0.005em]">
              <MaskLines lines={["Small lots.", <em key="i" className="italic text-forest">Distinct origins.</em>]} />
            </h2>
            <div className="md:col-span-5 md:col-start-8">
              <Reveal>
                <p className="text-base md:text-lg leading-relaxed text-smoke" data-testid="coffee-intro">
                  We look for coffees that carry a clear sense of where they come from — shaped
                  by their geography, harvest and the people who produce them.
                </p>
              </Reveal>
            </div>
          </div>
        </div>

        <div className="mt-20 md:mt-32 space-y-24 md:space-y-36">
          {STAGES.map((s, si) => (
            <div
              key={s.n}
              className="grid md:grid-cols-12 gap-10 md:gap-8 items-end max-w-[1400px] mx-auto"
              data-testid={s.testid}
            >
              <div className={`md:col-span-4 ${si === 1 ? "md:order-2 md:col-start-9" : ""}`}>
                <Reveal>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-brown font-medium">
                    {s.n} — {s.name}
                  </p>
                </Reveal>
                <h3 className="mt-5 font-serif font-normal text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.12]">
                  <MaskLines lines={[s.statement]} delay={0.1} />
                </h3>
                <Reveal delay={0.15}>
                  <p className="mt-6 text-base leading-relaxed text-smoke">{s.body}</p>
                </Reveal>
              </div>
              <div className={`md:col-span-8 ${si === 1 ? "md:order-1" : ""}`}>
                <div className={`grid gap-6 ${s.images.length > 1 ? "grid-cols-1 sm:grid-cols-5" : ""}`}>
                  {s.images.map((im, ii) => (
                    <Reveal
                      key={im.src}
                      delay={ii * 0.12}
                      className={s.images.length > 1 ? (ii === 0 ? "sm:col-span-2" : "sm:col-span-3") : ""}
                    >
                      <ImageReveal src={im.src} alt={im.alt} className={im.className} delay={si * 0.08 + ii * 0.1} testid={`${s.testid}-image-${ii}`} imgClassName="hover:scale-[1.03] transition-transform duration-[1400ms] ease-out" />
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 md:mt-36 max-w-[1200px] mx-auto" data-testid="coffee-timeline">
          <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-0">
            {STEPS.map((step, i) => (
              <div key={step} className="flex items-center gap-5 md:gap-0 md:contents">
                <Reveal delay={i * 0.12} className="md:flex-1 text-center">
                  <p className="font-sans text-[11px] text-brown">{String(i + 1).padStart(2, "0")}</p>
                  <p
                    className={`mt-2 font-serif text-2xl md:text-3xl ${
                      step === "Gone" ? "text-ink/35 line-through decoration-terracotta/70 decoration-1" : "text-ink"
                    }`}
                    data-testid={`timeline-step-${step.toLowerCase()}`}
                  >
                    {step}
                  </p>
                </Reveal>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block h-px flex-1 bg-ink/15 relative">
                    <span
                      className="absolute inset-0 bg-forest origin-left scale-x-100"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
